import { ShopZoneRow } from "../../api/shopZone";

/**
 * 지도 화면이 쓰는 안전구역 모양.
 * 예전 단독 HTML(임대사업소_지도_카카오.html)의 필드 이름을 그대로 따른다.
 */
export interface Zone {
  /** shop_zone_id. 한 사업소에 구역이 여럿일 수 있어 ogdp_id 가 아니라 이것으로 구분한다 */
  id: number;
  ogdp: string;
  nm: string;
  lat: number;
  lot: number;
  r: number;
  types: string;
  addr: string;
  /** 운행기록 추출좌표와의 거리(m). zone_rmrk 에 남아 있을 때만 */
  diff: number | null;
  sido: string;
  sgg: string;
  mdfcn: string;
}

/** 운행기록 추출좌표와 이 이상 벌어지면 재확인 대상 */
export const FAR = 1000;
export const RADIUS_MIN = 10;
export const RADIUS_MAX = 5000;

/** DB 컬럼이 decimal(10,8)/(11,8) 이라 8자리로 맞춘다 */
export const fix8 = (v: number) => +(+v).toFixed(8);

/** 두 좌표 사이 거리(m). 하버사인. 게이트웨이 shopZone.js 와 같은 계산이다 */
export function distM(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.min(1, Math.sqrt(a))));
}

/**
 * zone_rmrk 는 "출처 | 도로명주소 | 기준일 … | 지도보정 …" 처럼 쌓여 있다.
 * 둘째 칸이 주소, 셋째 칸에 "추출좌표와 NNNm" 가 있으면 그 거리를 쓴다.
 */
export function toZone(row: ShopZoneRow): Zone {
  const parts = (row.zone_rmrk ?? "").split("|").map((p) => p.trim());
  const addr = parts[1] ?? "";
  const m = parts[2] ? parts[2].match(/(\d+)\s*m/) : null;
  let sido = row.sd_nm ?? "";
  let sgg = row.sgg_nm ?? "";
  if (!sido && addr) {
    const bits = addr.split(/\s+/);
    sido = bits[0] ?? "";
    sgg = bits[1] ?? "";
  }
  return {
    id: row.shop_zone_id,
    ogdp: row.ogdp_id,
    nm: row.zone_nm,
    lat: Number(row.lat),
    lot: Number(row.lot),
    r: Number(row.radius_m),
    types: row.apply_ocrn_type_cd,
    addr,
    diff: m ? Number(m[1]) : null,
    sido,
    sgg,
    mdfcn: row.mdfcn_dt,
  };
}

export const isFar = (z: Zone) => z.diff !== null && z.diff >= FAR;

/** 이번 접속에서 처음 받은 값과 다른가 */
export const isChanged = (z: Zone, orig?: Zone) =>
  !!orig &&
  (Math.abs(orig.lat - z.lat) > 1e-9 ||
    Math.abs(orig.lot - z.lot) > 1e-9 ||
    orig.r !== z.r);

export const movedM = (z: Zone, orig?: Zone) =>
  orig ? distM(orig.lat, orig.lot, z.lat, z.lot) : 0;

export function stamp() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(
    d.getHours()
  )}${p(d.getMinutes())}`;
}

// ── 엑셀 불러오기 ─────────────────────────────────────────────
/* 이 화면이나 예전 HTML 에서 내보낸 엑셀을 그대로 되받는다.
   '수정분' 시트든 '전체' 시트든, 손으로 만든 표든 열 이름만 맞으면 읽는다. */

const norm = (h: string) => String(h).replace(/[\s_()]/g, "").toLowerCase();

// 열 이름은 사람마다 조금씩 다르게 적는다. 쓰이는 이름을 모두 받아준다
const COLS: Record<string, string[]> = {
  zid: ["shopzoneid", "구역id", "안전구역id"],
  ogdp: ["ogdpid", "ogdp", "그룹id", "지자체id"],
  nm: ["사업소명", "zonenm", "구역명", "명칭"],
  lat: ["수정위도", "위도", "lat", "최종위도"],
  lot: ["수정경도", "경도", "lot", "lng", "lon", "최종경도"],
  r: ["수정반경", "반경m", "반경", "radiusm", "radius", "최종반경"],
};

export function pickCols(header: string[]) {
  const found: Record<string, string> = {};
  const seen = header.map(norm);
  for (const [key, names] of Object.entries(COLS)) {
    for (const want of names) {
      const i = seen.indexOf(want);
      if (i >= 0) {
        found[key] = header[i];
        break;
      }
    }
  }
  return found;
}

export interface ImportPatch {
  zone: Zone;
  lat: number;
  lot: number;
  r: number;
}

export interface ImportPlan {
  sheet: string;
  patches: ImportPatch[];
  nochange: number;
  bad: number;
  missing: string[];
  ambiguous: string[];
}

/**
 * 엑셀 행을 구역에 맞춘다. shop_zone_id 열이 있으면 그것으로,
 * 없으면 ogdp_id 로 찾고 한 사업소에 구역이 여럿이면 사업소명까지 맞춘다.
 */
export function planImport(
  sheet: string,
  rows: Record<string, unknown>[],
  cols: Record<string, string>,
  zones: Zone[]
): ImportPlan {
  const byId = new Map(zones.map((z) => [z.id, z]));
  const byOgdp = new Map<string, Zone[]>();
  for (const z of zones) {
    byOgdp.set(z.ogdp, [...(byOgdp.get(z.ogdp) ?? []), z]);
  }
  const plan: ImportPlan = { sheet, patches: [], nochange: 0, bad: 0, missing: [], ambiguous: [] };
  const taken = new Set<number>();

  for (const row of rows) {
    let zone: Zone | undefined;
    const zid = cols.zid ? Number(row[cols.zid]) : NaN;
    const ogdp = cols.ogdp && row[cols.ogdp] != null ? String(row[cols.ogdp]).trim() : "";
    if (Number.isFinite(zid) && zid > 0) {
      zone = byId.get(zid);
      if (!zone) {
        plan.missing.push(String(zid));
        continue;
      }
    } else if (ogdp) {
      const cands = byOgdp.get(ogdp) ?? [];
      if (cands.length === 0) {
        plan.missing.push(ogdp);
        continue;
      }
      if (cands.length === 1) zone = cands[0];
      else {
        const nm = cols.nm && row[cols.nm] != null ? String(row[cols.nm]).trim() : "";
        zone = cands.find((c) => c.nm === nm);
        if (!zone) {
          plan.ambiguous.push(ogdp);
          continue;
        }
      }
    } else continue;

    const lat = cols.lat ? Number(row[cols.lat]) : NaN;
    const lot = cols.lot ? Number(row[cols.lot]) : NaN;
    const r = cols.r ? Number(row[cols.r]) : NaN;
    let nLat = zone.lat;
    let nLot = zone.lot;
    let nR = zone.r;
    // 좌표는 짝으로만 받는다. 한쪽만 있으면 엉뚱한 곳으로 간다
    if (Number.isFinite(lat) && Number.isFinite(lot)) {
      if (Math.abs(lat) > 90 || Math.abs(lot) > 180) {
        plan.bad++;
        continue;
      }
      nLat = fix8(lat);
      nLot = fix8(lot);
    }
    if (Number.isFinite(r)) {
      if (r < RADIUS_MIN || r > RADIUS_MAX) {
        plan.bad++;
        continue;
      }
      nR = Math.round(r);
    }
    const same =
      Math.abs(nLat - zone.lat) < 1e-9 && Math.abs(nLot - zone.lot) < 1e-9 && nR === zone.r;
    if (same || taken.has(zone.id)) {
      plan.nochange++;
      continue;
    }
    taken.add(zone.id);
    plan.patches.push({ zone, lat: nLat, lot: nLot, r: nR });
  }
  return plan;
}
